<template>
    <section id="projects" class="py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-16">
          <p class="text-emerald-600 text-sm font-semibold tracking-widest uppercase mb-3">Portfolio</p>
          <h2 class="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Featured <span class="text-emerald-600">Projects</span>
          </h2>
          <p class="text-slate-600 max-w-2xl mx-auto">
         A selection of projects I’ve worked on, showcasing my approach to problem-solving, system design, and building reliable software solutions.
          </p>
        </div>
        
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="project in visibleProjects" :key="project.id" 
               class="group bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300">
            <!-- Project Media -->
            <div class="relative h-52 bg-slate-900 overflow-hidden cursor-pointer" @click="$emit('open-modal', project)">
              <img v-if="project.mediaType === 'image'" 
                   :src="project.media" 
                   :alt="project.title" 
                   @click.stop="$emit('view-image', project.media)"
                   class="w-full h-full object-contain bg-slate-900 group-hover:scale-105 transition-transform duration-500 cursor-zoom-in" />
              <div v-else-if="project.mediaType === 'video'" class="relative w-full h-full">
                <video :src="project.media" 
                       class="w-full h-full object-cover" 
                       muted loop></video>
                <div class="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                  <div class="w-12 h-12 bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div class="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent pointer-events-none"></div>
            </div>
            
            <!-- Project Info -->
            <div class="p-6">
              <h3 class="text-xl font-bold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
                {{ project.title }}
              </h3>
              <p class="text-slate-600 text-sm mb-4 clamp-2 leading-relaxed">
                {{ project.shortDescription }}
              </p>
              <div class="flex flex-wrap gap-2 mb-5">
                <span v-for="tech in project.technologies.slice(0, 3)" 
                      :key="tech" 
                      class="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                  {{ tech }}
                </span>
              </div>
              <div class="flex gap-3">
                <a v-if="project.link" 
                   :href="project.link" 
                   target="_blank" 
                   class="flex-1 px-4 py-2.5 bg-emerald-500 text-white text-center text-sm rounded-lg font-semibold hover:bg-emerald-600 transition-colors">
                  View Live
                </a>
                <button @click="$emit('open-modal', project)" 
                        class="flex-1 px-4 py-2.5 bg-slate-100 text-slate-700 text-sm rounded-lg font-semibold hover:bg-slate-200 transition-colors">
                  Details
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- See More Button -->
        <div v-if="projects.length > 6" class="mt-12 text-center">
            <button @click="$emit('open-all-projects')" 
                    class="px-8 py-3 bg-white border border-slate-200 text-slate-700 font-semibold rounded-lg hover:border-emerald-500 hover:text-emerald-600 transition-all duration-300 shadow-sm hover:shadow-md">
                See More Projects
            </button>
        </div>
      </div>
    </section>
</template>

<script setup>
/* eslint-disable no-undef */
import { computed } from 'vue'
import { projects } from '../data/portfolio.js'

defineEmits(['open-modal', 'open-all-projects', 'view-image'])

const visibleProjects = computed(() => {
    return projects.slice(0, 6)
})
</script>
